import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { useAppSelector } from 'src/redux/hooks';

import useTrackClock from './hooks/useTrackClock';
import { readTrackMapPreferences, writeTrackMapPreferences } from './track.preferences';
import { addTimezoneSourceAndLayers, refreshTimezoneClockLabels, setTimezoneLayersVisibility, TIMEZONE_LABEL_LAYER_ID, TIMEZONE_SOURCE_ID } from './track.timezone';
import { ITrackMapContext, TrackMapContext } from './TrackMapContext';

// Single shared Mapbox instance for /track (list) and /track/:id (detail).
//
// One `new mapboxgl.Map(...)` lives here, attached to a <div> that lives in a
// hidden stash inside this layout. Child routes call `attachMapTo(target)` to
// DOM-move that div into their existing map slot, and `returnToStash()` on
// unmount so the instance stays alive across navigation. Children keep all of
// their existing JSX — only the `new mapboxgl.Map(...)` call in their map-init
// effect is replaced with context calls.
const TrackMapLayout = () => {
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const persistentMapDivRef = useRef<HTMLDivElement | null>(null);
    const stashRef = useRef<HTMLDivElement | null>(null);
    // State (not ref) so children re-render and their setup effects re-run when
    // the layout's map finishes loading after the child has already mounted.
    const [isMapReady, setIsMapReady] = useState(false);
    // Shared clock: the same `now` the header clock renders, so the map's timezone
    // labels can never drift out of step with it (see the label effect below).
    const { now: clockNow } = useTrackClock();
    const currentUser = useAppSelector(currentUserSelector);
    const userId: number | string | undefined = currentUser?.id;
    // Read the saved view synchronously so the map boots in the user's last style
    // rather than flashing the default first. `auth` is redux-persisted, so
    // `currentUser.id` is normally already known on the first render; the
    // hydration effect below covers the case where it lands later.
    const initialPreferencesRef = useRef(readTrackMapPreferences(userId));

    // ── Map appearance (theme + timezone overlay) ─────────────────────────────
    // Owned here, not in either page, because both pages share this one map.
    // Both are persisted per user in localStorage (see track.preferences.ts) so the
    // last-selected view survives a reload. localStorage ONLY — no backend copy.
    const [mapStyle, setMapStyleState] = useState<string>(initialPreferencesRef.current.mapStyle);
    const [showTimezones, setShowTimezones] = useState<boolean>(initialPreferencesRef.current.showTimezones);
    // Guards the hydration effect so the stored value is applied only once per
    // user — after that the user's own toggles are authoritative.
    const hydratedForUserRef = useRef<number | string | null>(null);
    // Bumped after every completed style load so each page can re-add its own
    // sources/layers (setStyle destroys them) from a useEffect([styleEpoch]).
    const [styleEpoch, setStyleEpoch] = useState<number>(0);

    // Ref mirror: the map-creation effect runs once, and its persistent
    // 'style.load' listener must read the CURRENT toggle value, not the value
    // captured when the listener was registered.
    const showTimezonesRef = useRef(showTimezones);
    useEffect(() => {
        showTimezonesRef.current = showTimezones;
    }, [showTimezones]);
    // Same mirror for the style, so the once-per-user hydration effect can compare
    // against the CURRENT style without listing mapStyle as a dependency (which would
    // re-run it on every theme change).
    const mapStyleRef = useRef(mapStyle);
    useEffect(() => {
        mapStyleRef.current = mapStyle;
    }, [mapStyle]);

    useEffect(() => {
        if (mapRef.current) return;

        if (window?.MAPBOX_ACCESSTOKEN) {
            mapboxgl.accessToken = window.MAPBOX_ACCESSTOKEN;
        }

        // The persistent map DOM node. Sized 100%×100% so it fills whichever
        // slot it gets attached to. Lives in the stash between page transitions.
        const div = document.createElement('div');
        div.style.width = '100%';
        div.style.height = '100%';
        persistentMapDivRef.current = div;

        if (stashRef.current) {
            stashRef.current.appendChild(div);
        }

        mapRef.current = new mapboxgl.Map({
            container: div,
            // Saved style (falls back to TRACK_DEFAULT_MAP_STYLE inside the helper)
            // so the map never renders the default theme first and then swaps.
            style: initialPreferencesRef.current.mapStyle,
            center: [-112.1404, 42.851],
            zoom: 3.3649,
            pitch: 0,
            bearing: 0,
            boxZoom: true,
            antialias: true,
            projection: 'mercator',
            dragRotate: false,
        });

        // Added ONCE here (not per-page) so it's the same DOM element throughout
        // /track <-> /track/:id navigation — mirrors wal-frontend-v2's RadarMapLayout.
        // Previously the detail page added/removed its own NavigationControl on
        // mount/unmount while the list page hand-built separate zoom buttons; the
        // swap between those two different implementations on every navigation is
        // what read as a "flicker" in the control's position.
        mapRef.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

        mapRef.current.on('moveend', () => {
            console.log('center:', mapRef.current.getCenter(), 'zoom:', mapRef.current.getZoom());
        });

        // Mark ready once Mapbox finishes its initial style load. The state
        // change re-renders children so their map-setup effects (route line,
        // cluster source) re-run with isMapReady === true.
        mapRef.current.on('load', () => {
            setIsMapReady(true);
        });

        // Timezone overlay lives on the LAYOUT's map instance so ONE
        // instance-level style.load listener covers every style load: the
        // initial one AND every theme change from either page (setStyle wipes
        // all style-owned sources/layers). Instance listeners survive setStyle,
        // and this one is registered before any child mounts — so it runs ahead
        // of the pages' styleEpoch effects, letting the overlay's layers land
        // UNDERNEATH the cluster/route layers those pages re-add.
        mapRef.current.on('style.load', () => {
            if (showTimezonesRef.current && mapRef.current) {
                void addTimezoneSourceAndLayers(mapRef.current, () => showTimezonesRef.current);
            }
        });

        return () => {
            mapRef.current?.remove();
            mapRef.current = null;

            if (div.parentNode) {
                div.parentNode.removeChild(div);
            }

            persistentMapDivRef.current = null;
            setIsMapReady(false);
        };
    }, []);

    const attachMapTo = useCallback((target: HTMLElement | null) => {
        const node = persistentMapDivRef.current;

        if (!target || !node) return;
        if (node.parentNode === target) return;

        target.appendChild(node);
        // Container size likely changed during the DOM move — Mapbox needs a
        // resize() so the canvas + tile sizing recompute correctly.
        mapRef.current?.resize();
    }, []);

    const returnToStash = useCallback(() => {
        const node = persistentMapDivRef.current;
        const stash = stashRef.current;

        if (!node || !stash) return;
        if (node.parentNode === stash) return;

        stash.appendChild(node);
    }, []);

    // Swap the base style. setStyle() asynchronously replaces the style and WIPES
    // every style-owned source and layer, so:
    //   1. drop stale DOM markers first — mapboxgl.Marker elements are NOT part of
    //      the style, so they would otherwise freeze on screen with no source
    //      behind them and never be garbage-collected
    //   2. bump styleEpoch only AFTER the new style has loaded, which is the
    //      signal each page waits on to re-add its own sources/layers
    // Map event listeners are NOT re-bound here — they survive setStyle, so
    // re-registering would double-bind every handler.
    const setMapStyle = useCallback(
        (styleUrl: string) => {
            const map = mapRef.current;

            if (!map || styleUrl === mapStyle) return;

            (map as any)._clearAllMarkers?.();
            setMapStyleState(styleUrl);
            writeTrackMapPreferences(userId, { mapStyle: styleUrl });
            map.setStyle(styleUrl);

            // `once` so a rapid sequence of theme switches doesn't stack handlers.
            map.once('style.load', () => {
                setStyleEpoch((n) => n + 1);
            });
        },
        [mapStyle, userId]
    );

    // Apply the saved view once per user, in case `currentUser.id` was not yet
    // available when the useState initialisers ran (a fresh login with no persisted
    // auth). Runs once per user id (hydratedForUserRef) so later currentUser
    // refreshes can't clobber toggles the user just made.
    useEffect(() => {
        if (!userId || hydratedForUserRef.current === userId) return;

        hydratedForUserRef.current = userId;

        const saved = readTrackMapPreferences(userId);
        const map = mapRef.current;

        // Map side effects are done HERE, not inside the state updaters: React may
        // invoke an updater twice (StrictMode), and a doubled setStyle would bump
        // styleEpoch twice and make every page re-add its layers twice.
        if (showTimezonesRef.current !== saved.showTimezones) {
            showTimezonesRef.current = saved.showTimezones;
            setShowTimezones(saved.showTimezones);

            if (map && saved.showTimezones && !map.getSource(TIMEZONE_SOURCE_ID)) {
                void addTimezoneSourceAndLayers(map, () => showTimezonesRef.current);
            } else if (map) {
                setTimezoneLayersVisibility(map, saved.showTimezones);
            }
        }

        if (mapStyleRef.current !== saved.mapStyle) {
            mapStyleRef.current = saved.mapStyle;
            setMapStyleState(saved.mapStyle);

            if (map) {
                // Same sequence as setMapStyle below: drop stale DOM markers, swap the
                // style, and bump styleEpoch once loaded so each page re-adds its layers.
                (map as any)._clearAllMarkers?.();
                map.setStyle(saved.mapStyle);
                map.once('style.load', () => {
                    setStyleEpoch((n) => n + 1);
                });
            }
        }
    }, [userId]);

    // Keep the overlay's per-timezone clock labels in step with the header clock.
    //
    // Both read the SAME `now` (useTrackClock, which ticks every second) rather than
    // each running its own timer: with separate timers the map sat up to a minute
    // behind the header, because the labels were seeded with the Date.now() of
    // whenever the source happened to be added and only advanced on the next
    // minute boundary.
    //
    // The labels still only CHANGE once a minute — they render h:mm — so the write
    // is gated on the minute actually having changed. That keeps the label rewrite
    // (a text-field expression over all 40 zones) at once per minute while the value
    // itself is never more than a second stale.
    const lastLabelMinuteRef = useRef<number | null>(null);

    useEffect(() => {
        if (!showTimezones) {
            // Force a re-seed on the next enable rather than trusting a minute stamp
            // from before the overlay was hidden.
            lastLabelMinuteRef.current = null;

            return;
        }

        const map = mapRef.current;

        if (!map) return;

        // Until the (async) label layer exists there is nothing to write to, and
        // refreshTimezoneClockLabels no-ops. Don't record the minute in that case,
        // or the write would be skipped once the layer finally lands and the labels
        // would keep the value seeded at add time for up to a minute.
        if (!map.getLayer(TIMEZONE_LABEL_LAYER_ID)) return;

        const minuteStamp = Math.floor(clockNow.getTime() / 60000);

        if (lastLabelMinuteRef.current === minuteStamp) return;

        lastLabelMinuteRef.current = minuteStamp;
        refreshTimezoneClockLabels(map, clockNow.getTime());
        // isMapReady re-runs this once the map instance exists.
    }, [showTimezones, isMapReady, clockNow]);

    const toggleTimezones = useCallback(() => {
        const next = !showTimezonesRef.current;
        showTimezonesRef.current = next;
        setShowTimezones(next);
        writeTrackMapPreferences(userId, { showTimezones: next });

        const map = mapRef.current;

        if (!map) return;

        if (next && !map.getSource(TIMEZONE_SOURCE_ID)) {
            // First enable this session — fetch the data and add the layers.
            // Pass the live ref (not `next`): if the user toggles off again before
            // the ~271KB fetch resolves, the layers are added hidden rather than
            // flashing on. Closes the rapid-toggle race.
            void addTimezoneSourceAndLayers(map, () => showTimezonesRef.current);
        } else {
            setTimezoneLayersVisibility(map, next);
        }
    }, [userId]);

    const ctxValue = useMemo<ITrackMapContext>(
        () => ({
            mapRef,
            isMapReady,
            attachMapTo,
            returnToStash,
            mapStyle,
            setMapStyle,
            styleEpoch,
            showTimezones,
            toggleTimezones,
        }),
        [isMapReady, attachMapTo, returnToStash, mapStyle, setMapStyle, styleEpoch, showTimezones, toggleTimezones]
    );

    return (
        <TrackMapContext.Provider value={ctxValue}>
            {/* Hidden stash — persistent map node lives here between page
                transitions. display:none keeps it out of layout while the
                Mapbox instance stays alive. */}
            <div ref={stashRef} style={{ display: 'none' }} aria-hidden />
            <Outlet />
        </TrackMapContext.Provider>
    );
};

export default TrackMapLayout;
